import type { ReactNode } from "react";
import { AlertCircle, Info, AlertTriangle, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

type CalloutVariant = "info" | "warning" | "danger" | "success";

interface CalloutProps {
  variant?: CalloutVariant;
  title?: string;
  children: ReactNode;
  className?: string;
  icon?: ReactNode;
}

const variantStyles: Record<CalloutVariant, { container: string; icon: React.ComponentType<{ className?: string }> }> = {
  info: {
    container: "border-[var(--callout-info-border)] bg-[var(--callout-info-bg)] text-[var(--callout-info-fg)]",
    icon: Info,
  },
  warning: {
    container: "border-[var(--callout-warning-border)] bg-[var(--callout-warning-bg)] text-[var(--callout-warning-fg)]",
    icon: AlertTriangle,
  },
  danger: {
    container: "border-[var(--callout-danger-border)] bg-[var(--callout-danger-bg)] text-[var(--callout-danger-fg)]",
    icon: AlertCircle,
  },
  success: {
    container: "border-[var(--callout-success-border)] bg-[var(--callout-success-bg)] text-[var(--callout-success-fg)]",
    icon: CheckCircle2,
  },
};

export function Callout({ variant = "info", title, children, className, icon }: CalloutProps) {
  const { container, icon: DefaultIcon } = variantStyles[variant];
  return (
    <div className={cn("flex items-start gap-2.5 rounded-md border p-3 text-sm", container, className)}>
      <span className="mt-0.5 shrink-0">
        {icon ?? <DefaultIcon className="h-4 w-4" />}
      </span>
      <div className="min-w-0 flex-1">
        {title && <p className="font-medium mb-0.5">{title}</p>}
        <div className={cn(title ? "text-[0.9em] opacity-90" : "")}>{children}</div>
      </div>
    </div>
  );
}
